import { Button, Dialog, Input, Select } from '@artosera/ui';
import { formatDateRange, TYPES_EXPOSITION, type TypeExposition } from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useFermetureGardee } from '../useFermetureGardee.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { ChampsManquants } from '../ChampsManquants.js';
import { LIMITES } from '../validations.js';
import { useTranslation } from 'react-i18next';
import { useCreerExposition } from '../../api/expositions.js';
import { useLieux } from '../../api/lieux.js';
import { Field } from '../Field.js';
import styles from './NouvelleExpositionDialog.module.css';

export interface NouvelleExpositionDialogProps {
  galerieSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreee: (id: string) => void;
}

/**
 * Dialogue de création (3.3, point 4, S3-d) : type, titre, dates, lieu ou
 * foire + stand — le stand ne se déclare que sur une foire (§ 15.4, refusé
 * ailleurs par l'API : `400 stand_hors_foire`).
 */
export function NouvelleExpositionDialog({
  galerieSlug,
  open,
  onOpenChange,
  onCreee,
}: NouvelleExpositionDialogProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';

  const [type, setType] = useState<TypeExposition>('exposition');
  const [titre, setTitre] = useState('');
  const [debutLe, setDebutLe] = useState('');
  const [finLe, setFinLe] = useState('');
  const [lieuId, setLieuId] = useState('');
  const [stand, setStand] = useState('');
  const formulaire = useErreurFormulaire();
  const [enCours, setEnCours] = useState(false);

  const lieux = useLieux(galerieSlug);
  const creer = useCreerExposition(galerieSlug);

  const estFoire = type === 'foire';
  const datesValides = debutLe !== '' && finLe !== '' && debutLe <= finLe;
  const peutSoumettre = titre.trim().length > 0 && datesValides && !enCours;
  const finAvantDebut = debutLe !== '' && finLe !== '' && finLe < debutLe;
  const manques = [
    titre.trim().length === 0 ? t('regie:expositions.nouvelle.titreChamp') : null,
    debutLe === '' ? t('regie:expositions.nouvelle.debutChamp') : null,
    finLe === '' ? t('regie:expositions.nouvelle.finChamp') : null,
  ].filter((champ): champ is string => champ !== null);
  const lieuNom = lieux.data?.find((lieu) => lieu.id === lieuId)?.nom ?? null;

  const garde = useFermetureGardee(
    [titre, debutLe, finLe, lieuId, stand].some((valeur) => valeur.trim().length > 0),
    onOpenChange,
  );

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    setEnCours(true);
    try {
      const exposition = await creer.mutateAsync({
        type,
        titre: { fr: titre },
        debutLe,
        finLe,
        ...(lieuId ? { lieuId } : {}),
        ...(estFoire && stand ? { stand } : {}),
      });
      onOpenChange(false);
      onCreee(exposition.exposition.id);
    } catch (error) {
      formulaire.appliquer(error, ['titre', 'debutLe', 'finLe', 'stand']);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={garde.onOpenChange}
        title={t('regie:expositions.nouvelle.titre')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button onClick={() => void soumettre()} loading={enCours} disabled={!peutSoumettre}>
              {enCours
                ? t('regie:expositions.nouvelle.creation')
                : t('regie:expositions.nouvelle.creer')}
            </Button>
          </>
        }
      >
        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          {/* Carton d'invitation en cours de composition : reflet visuel de la
            saisie, ignoré des lecteurs d'écran (les champs disent tout). */}
          <div className={styles.carton} data-type={type} aria-hidden>
            <span className={styles.cartonType}>{t(`regie:expositions.types.${type}`)}</span>
            <span className={styles.cartonTitre} data-vide={titre.trim() ? 'false' : 'true'}>
              {titre.trim()}
            </span>
            <span className={styles.cartonFilet} />
            <span className={styles.cartonLigne}>
              {datesValides ? formatDateRange(new Date(debutLe), new Date(finLe), langue) : ''}
            </span>
            <span className={styles.cartonLigne}>
              {estFoire && stand.trim()
                ? t('regie:expositions.carte.stand', { stand: stand.trim() })
                : (lieuNom ?? '')}
            </span>
          </div>

          <Field
            label={t('regie:expositions.nouvelle.typeChamp')}
            htmlFor="nouvelle-exposition-type"
          >
            <Select
              id="nouvelle-exposition-type"
              aria-label={t('regie:expositions.nouvelle.typeChamp')}
              value={type}
              onValueChange={(valeur) => {
                setType(valeur as TypeExposition);
                if (valeur !== 'foire') setStand('');
              }}
              options={TYPES_EXPOSITION.map((code) => ({
                value: code,
                label: t(`regie:expositions.types.${code}`),
              }))}
            />
          </Field>

          <Field
            label={t('regie:expositions.nouvelle.titreChamp')}
            htmlFor="nouvelle-exposition-titre"
            error={formulaire.messageChamp('titre')}
          >
            <Input
              id="nouvelle-exposition-titre"
              required
              maxLength={300}
              value={titre}
              onChange={(event) => setTitre(event.target.value)}
            />
          </Field>

          <div className={styles.periode}>
            <Field
              label={t('regie:expositions.nouvelle.debutChamp')}
              htmlFor="nouvelle-exposition-debut"
              error={formulaire.messageChamp('debutLe')}
            >
              <Input
                id="nouvelle-exposition-debut"
                type="date"
                required
                value={debutLe}
                onChange={(event) => setDebutLe(event.target.value)}
              />
            </Field>
            <span className={styles.periodeFleche} aria-hidden>
              →
            </span>
            <Field
              label={t('regie:expositions.nouvelle.finChamp')}
              htmlFor="nouvelle-exposition-fin"
              error={
                finAvantDebut
                  ? t('regie:validation.finAvantDebut')
                  : formulaire.messageChamp('finLe')
              }
            >
              <Input
                id="nouvelle-exposition-fin"
                type="date"
                required
                value={finLe}
                onChange={(event) => setFinLe(event.target.value)}
              />
            </Field>
          </div>

          <div className={styles.ou} data-foire={estFoire ? 'true' : 'false'}>
            <Field
              label={t('regie:expositions.nouvelle.lieuChamp')}
              htmlFor="nouvelle-exposition-lieu"
            >
              <Select
                id="nouvelle-exposition-lieu"
                aria-label={t('regie:expositions.nouvelle.lieuChamp')}
                value={lieuId}
                onValueChange={setLieuId}
                placeholder={t('regie:expositions.nouvelle.lieuChoisir')}
                options={[
                  { value: '', label: t('regie:expositions.nouvelle.lieuChoisir') },
                  ...(lieux.data?.map((lieu) => ({ value: lieu.id, label: lieu.nom })) ?? []),
                ]}
              />
            </Field>

            {estFoire ? (
              <Field
                label={t('regie:expositions.nouvelle.standChamp')}
                htmlFor="nouvelle-exposition-stand"
                error={formulaire.messageChamp('stand')}
              >
                <Input
                  id="nouvelle-exposition-stand"
                  maxLength={LIMITES.exposition.stand}
                  value={stand}
                  onChange={(event) => setStand(event.target.value)}
                />
                <p className={styles.aide}>{t('regie:expositions.nouvelle.standAide')}</p>
              </Field>
            ) : null}
          </div>

          <ChampsManquants champs={finAvantDebut ? [] : manques} />
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {formulaire.erreur}
            </p>
          ) : null}
        </form>
      </Dialog>
      {garde.confirmation}
    </>
  );
}
